import { gql } from "@apollo/client";
import {
  AppSessionId,
  CompletionDataFrameSchema,
  HexVersionId,
  TypeaheadV2Config,
  sleep,
} from "@hex/common";
import { CellId, MagicCompletionEventId } from "@hex/common";
import { isEmpty } from "lodash";
import {
  CancellationToken,
  editor as Editor,
  Position,
  Range,
  languages,
} from "monaco-editor";

import { client } from "../../../client";
import { CompletionLanguage } from "../../../generated/graphqlTypes";
import { RootStore } from "../../../redux/hooks";
import { appSessionMPSelectors } from "../../../redux/slices/appSessionMPSlice";
import {
  CellMP,
  hexVersionMPSelectors,
} from "../../../redux/slices/hexVersionMPSlice";
import { editHistoryManager } from "../../../state/models/editHistoryTracker";
import { getCellIdForModel } from "../../../state/models/useModel";

export interface TypeaheadCompletionData {
  context: string;
  prefix: string;
  suffix: string;
  cellId: CellId;
  completion: string;
  completionId: MagicCompletionEventId;
  accepted: boolean;
  displayed: boolean;
}
import {
  GetCompletionsV2Document,
  GetCompletionsV2Query,
  GetCompletionsV2QueryVariables,
  TrackInlineCompletionEventDisplayedDocument,
  TrackInlineCompletionEventReviewedDocument,
} from "./TypeaheadCompletionProvider.generated";
import { TRACK_TYPEAHEAD_COMPLETION_ACCEPTED_CMD } from "./useMonacoCompletion";

gql`
  query GetCompletionsV2(
    $language: CompletionLanguage!
    $editableRegion: String!
    $prefix: String!
    $suffix: String!
    $upstreamContext: String!
    $dataframeContext: [CompletionDataFrameSchema!]!
    $editHistoryEvents: [EditHistoryEvent!]!
  ) {
    getCompletionsV2(
      language: $language
      editableRegion: $editableRegion
      prefix: $prefix
      suffix: $suffix
      upstreamContext: $upstreamContext
      dataframeContext: $dataframeContext
      editHistoryEvents: $editHistoryEvents
    ) {
      result
      magicCompletionEvent {
        id
      }
    }
  }
`;

gql`
  mutation TrackInlineCompletionEventDisplayed(
    $completionId: MagicCompletionEventId!
  ) {
    trackInlineCompletionEventDisplayed(completionId: $completionId)
  }
`;

gql`
  mutation TrackInlineCompletionEventReviewed(
    $completionId: MagicCompletionEventId!
    $accepted: Boolean!
  ) {
    trackInlineCompletionEventReviewed(
      completionId: $completionId
      accepted: $accepted
    )
  }
`;

const LANGUAGE_MAP: Record<string, CompletionLanguage> = {
  python: "PYTHON",
  sql: "SQL",
  pgsql: "PGSQL",
  mysql: "MYSQL",
  redshift: "REDSHIFT",
  snowflake: "SNOWFLAKE",
};

const INLINE_COMPLETION_DEBOUNCE = 150;
const DEBUG = false;

// Quick little log helper to make it easy to commit debug logs
// until this is working really well. Its useful to understand
// why we're not getting completions when that happens.
const debugLog = (...args: unknown[]): void => {
  if (DEBUG) {
    // eslint-disable-next-line no-console -- I want to see these logs
    console.log(...args);
  }
};

export class TypeaheadCompletionProvider
  implements languages.InlineCompletionsProvider
{
  private hexVersionId?: HexVersionId;
  private appSessionId?: AppSessionId;
  private store?: RootStore;
  public lastCompletion?: TypeaheadCompletionData;
  private userSettingEnabled = false;
  private typeaheadV2Config?: TypeaheadV2Config;

  public update({
    appSessionId,
    hexVersionId,
    store,
    typeaheadV2Config,
  }: {
    hexVersionId: HexVersionId;
    appSessionId: AppSessionId;
    store: RootStore;
    typeaheadV2Config: TypeaheadV2Config;
  }): void {
    this.hexVersionId = hexVersionId;
    this.appSessionId = appSessionId;
    this.store = store;
    this.typeaheadV2Config = typeaheadV2Config;
  }

  public enableOrDisable(enabled: boolean): void {
    this.userSettingEnabled = enabled;
  }

  // eslint-disable-next-line max-params -- this is a monaco API method
  async provideInlineCompletions(
    model: Editor.ITextModel,
    position: Position,
    _context: languages.InlineCompletionContext,
    token: CancellationToken,
  ): Promise<languages.InlineCompletions | undefined> {
    // Do we have a previous completion that got displayed but wasnt accepted?
    if (
      this.lastCompletion != null &&
      this.lastCompletion.displayed &&
      !this.lastCompletion.accepted
    ) {
      // Soft rejection: User typed different characters, abandoning the previous completion
      // We leave accepted as null to distinguish from explicit ESC rejection
      this.lastCompletion = undefined;
    }

    await sleep(INLINE_COMPLETION_DEBOUNCE);
    if (token.isCancellationRequested || !this.userSettingEnabled) {
      debugLog(
        "Returning undefined because it was requested, or user setting not enabled",
        {
          tokenIsCancellationRequested: token.isCancellationRequested,
          userSettingEnabled: this.userSettingEnabled,
        },
      );
      return undefined;
    }

    if (
      this.hexVersionId == null ||
      this.store == null ||
      this.appSessionId == null
    ) {
      debugLog(
        "Returning undefined because hexVersionId, store, or appSessionId is null",
        {
          hexVersionId: this.hexVersionId,
          store: this.store,
          appSessionId: this.appSessionId,
        },
      );
      return undefined;
    }

    // Don't return anything if theres no text in the cell
    if (
      model.getValueLengthInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: model.getLineCount(),
        endColumn: model.getLineMaxColumn(model.getLineCount()),
      }) === 0
    ) {
      debugLog("Returning undefined because there's no text in the cell");
      return undefined;
    }

    // Ensure that typeahead is enabled by the user for the current cell
    const cellId = getCellIdForModel(model);
    if (cellId == null) {
      console.error("No cell matching model");
      return undefined;
    }
    const cells = hexVersionMPSelectors
      .getCellSelectors(this.hexVersionId)
      .selectFlattenedSorted(this.store.getState());

    const targetCellIndex = cells.findIndex((c) => c.id === cellId);
    const targetCell = cells[targetCellIndex];
    if (cells == null || !targetCell) {
      console.error("No cells found!");
      return undefined;
    }

    const upstreamContext = this.getUpstreamContext(
      cells,
      targetCellIndex,
      targetCell,
    );

    const dataframeContext = this.getDataFrameContext();

    const { editableRegion, prefix, suffix } = this.getRegions(model, position);

    const modelLanguage = model.getLanguageId();
    if (!(modelLanguage in LANGUAGE_MAP)) {
      debugLog(
        "Returning undefined because the model language is not supported",
        {
          modelLanguage,
        },
      );
      return undefined;
    }

    const language = LANGUAGE_MAP[modelLanguage];

    const editHistoryEvents = editHistoryManager.getAllRecentEditHistory(20);

    let result = undefined;
    try {
      debugLog("Getting completions", {
        upstreamContext,
        dataframeContext,
        prefix,
        editableRegion,
        suffix,
        editHistoryCount: editHistoryEvents.length,
      });
      const { data } = await client.query<
        GetCompletionsV2Query,
        GetCompletionsV2QueryVariables
      >({
        query: GetCompletionsV2Document,
        variables: {
          language,
          upstreamContext,
          dataframeContext,
          prefix,
          editableRegion,
          suffix,
          editHistoryEvents: editHistoryEvents,
        },
      });
      result = data.getCompletionsV2.result ?? "";

      const completionCellId = getCellIdForModel(model);
      if (completionCellId && data.getCompletionsV2.magicCompletionEvent) {
        this.lastCompletion = {
          completionId: data.getCompletionsV2.magicCompletionEvent.id,
          cellId: completionCellId,
          completion: result,
          context: upstreamContext,
          prefix,
          suffix,
          displayed: false,
          accepted: false,
        };
      }
    } catch (_e) {
      debugLog("Returning undefined because of error", { error: _e });
      return undefined;
    }

    if (isEmpty(result)) {
      debugLog("Returning undefined because the result is empty");
      return undefined;
    } else {
      debugLog("Got a result from the API!", result);
    }

    // Get the text on the current line
    const currentLineText = model.getValueInRange({
      startLineNumber: position.lineNumber,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: position.column,
    });

    // Check if the current line text is a prefix of the result
    if (!result.startsWith(currentLineText)) {
      debugLog(
        "Returning undefined because the result does not start with the current line text",
        {
          result,
          currentLineText,
        },
      );
      return undefined;
    }

    return {
      items: [
        {
          insertText: result,
          // Replace the current line with the new text
          range: new Range(
            position.lineNumber,
            1,
            position.lineNumber,
            model.getLineMaxColumn(position.lineNumber),
          ),
          command: {
            id: TRACK_TYPEAHEAD_COMPLETION_ACCEPTED_CMD,
            title: TRACK_TYPEAHEAD_COMPLETION_ACCEPTED_CMD,
            arguments: [this.lastCompletion],
          },
        },
      ],

      enableForwardStability: true,
    };
  }

  /**
   * Tracks that a typeahead completion was shown to the user.
   */
  handleItemDidShow(
    _completions: languages.InlineCompletions<languages.InlineCompletion>,
    _item: languages.InlineCompletion,
    _updatedInsertText: string,
  ): void {
    if (this.lastCompletion == null) return;

    this.lastCompletion.displayed = true;

    void client
      .mutate({
        mutation: TrackInlineCompletionEventDisplayedDocument,
        variables: {
          completionId: this.lastCompletion.completionId,
        },
      })
      .catch((error) => {
        console.error("Failed to track completion displayed:", error);
      });
  }

  /**
   * Tracks partial acceptance of a typeahead completion.
   */
  handlePartialAccept(): void {
    if (this.lastCompletion == null) return;

    this.lastCompletion.accepted = true;

    // Track partial acceptance same as full acceptance
    // TODO: Add more nuanced tracking to distinguish partial vs full acceptance
    void client
      .mutate({
        mutation: TrackInlineCompletionEventReviewedDocument,
        variables: {
          completionId: this.lastCompletion.completionId,
          accepted: true,
        },
      })
      .catch((error) => {
        console.error("Failed to track partial completion acceptance:", error);
      });
  }

  /**
   * Track dismissal of a typeahead completion.
   */
  freeInlineCompletions(
    _completions: languages.InlineCompletions<languages.InlineCompletion>,
  ): void {
    if (
      this.lastCompletion != null &&
      this.lastCompletion.displayed &&
      !this.lastCompletion.accepted
    ) {
      void client
        .mutate({
          mutation: TrackInlineCompletionEventReviewedDocument,
          variables: {
            completionId: this.lastCompletion.completionId,
            accepted: false,
          },
        })
        .catch((error) => {
          console.error("Failed to track completion dismissal:", error);
        });

      this.lastCompletion = undefined;
    }
  }

  private getUpstreamContext(
    cells: CellMP[],
    targetCellIndex: number,
    targetCell: CellMP,
  ): string {
    if (this.hexVersionId == null || this.store == null) {
      console.error(
        "Error getting upstream context. Completion will return, but will not have any context.",
        {
          error: "HexVersionId or store is null",
        },
      );
      return "";
    }

    // Get the upstream cells of the same type as the target cell to use as upstream
    // context for the user prompt.
    const upstreamCellsOfSameType = cells.filter(
      (c, index) =>
        index < targetCellIndex && c.cellType === targetCell.cellType,
    );
    const upstreamCellContents = hexVersionMPSelectors
      .getCellContentSelectors(this.hexVersionId)
      .selectByCellIds(
        this.store.getState(),
        upstreamCellsOfSameType.map((c) => c.id),
      );

    const upstreamCellSources = upstreamCellContents
      ? upstreamCellsOfSameType.map((c) => {
          const contents = upstreamCellContents[c.id];
          if (
            contents.__typename === "CodeCell" ||
            contents.__typename === "SqlCell" ||
            contents.__typename === "MarkdownCell"
          ) {
            const source = contents.source.trim();
            if (source.length > 0) {
              return source;
            }
          }
          return null;
        })
      : [];

    return upstreamCellSources.filter((elem) => !isEmpty(elem)).join("\n\n\n");
  }

  private getRegions(
    model: Editor.ITextModel,
    position: Position,
  ): {
    prefix: string;
    editableRegion: string;
    suffix: string;
  } {
    // Grab everything in the cell before the current line
    const prefix =
      position.lineNumber === 1
        ? ""
        : model.getValueInRange({
            startLineNumber: 1,
            startColumn: 1,
            endLineNumber: position.lineNumber - 1,
            endColumn: model.getLineMaxColumn(position.lineNumber - 1),
          });

    // Grab only the current line with the cursor
    const currentLineText = model.getValueInRange({
      startLineNumber: position.lineNumber,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: model.getLineMaxColumn(position.lineNumber),
    });

    // Insert cursor marker at the current cursor position
    const beforeCursor = currentLineText.substring(0, position.column - 1);
    const afterCursor = currentLineText.substring(position.column - 1);
    const editableRegion = `${beforeCursor}<|user_cursor_is_here|>${afterCursor}`;

    // Grab everything from the next line to the end
    const suffix =
      position.lineNumber === model.getLineCount()
        ? ""
        : model.getValueInRange({
            startLineNumber: position.lineNumber + 1,
            startColumn: 1,
            endLineNumber: model.getLineCount(),
            endColumn: model.getLineMaxColumn(model.getLineCount()),
          });

    return {
      prefix,
      editableRegion,
      suffix,
    };
  }

  private getDataFrameContext(): Array<CompletionDataFrameSchema> {
    if (this.appSessionId == null || this.store == null) {
      console.error(
        "Error getting data frame context. Completion will return, but will not have any context.",
        {
          error: "AppSessionId or store is null",
        },
      );
      return [];
    }

    const scopeInfo = appSessionMPSelectors
      .getScopeSelectors(this.appSessionId)
      .selectAll(this.store.getState());
    return (scopeInfo ?? [])
      .filter((item) => item.dataFrameSchema != null && !item.isHidden)
      .map((item) => ({
        name: item.name,
        schema: item.dataFrameSchema?.columns ?? {},
      }));
  }
}

export const TYPEAHEAD_COMPLETION_PROVIDER = new TypeaheadCompletionProvider();
